import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Link } from '@/lib/router-compat';
import { Calculator, ArrowRight, Info } from 'lucide-react';
import { LOAN_TERMS } from '@/config/company';

const currency = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

export default function LoanCalculator() {
  const [amount, setAmount] = useState(10000);
  const [term, setTerm] = useState(36);
  const [apr, setApr] = useState<number>(LOAN_TERMS.repApr);

  const { monthly, totalRepayable, totalInterest } = useMemo(() => {
    const r = apr / 100 / 12;
    const m = r === 0 ? amount / term : (amount * r) / (1 - Math.pow(1 + r, -term));
    const total = m * term;
    return { monthly: m, totalRepayable: total, totalInterest: total - amount };
  }, [amount, term, apr]);

  return (
    <section id="calculator" className="py-20 bg-muted/30">
      <div className="container mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent/10 text-accent rounded-full text-sm font-medium mb-4">
            <Calculator className="h-4 w-4" /> Loan Calculator
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
            Estimate your monthly repayment
          </h2>
          <p className="text-muted-foreground text-lg">
            Move the sliders to see an illustrative repayment. Your actual rate depends on your
            credit profile, income and loan term.
          </p>
        </div>

        <Card className="max-w-4xl mx-auto shadow-card">
          <CardContent className="p-6 md:p-10 grid md:grid-cols-2 gap-10">
            <div className="space-y-8">
              <div>
                <div className="flex justify-between items-baseline mb-3">
                  <label htmlFor="calc-amount" className="font-medium text-foreground">Loan amount</label>
                  <span className="font-display text-xl font-bold text-foreground">{currency(amount)}</span>
                </div>
                <Slider
                  id="calc-amount"
                  aria-label="Loan amount"
                  value={[amount]}
                  min={LOAN_TERMS.minAmount}
                  max={LOAN_TERMS.maxAmount}
                  step={500}
                  onValueChange={(v) => setAmount(v[0])}
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>{currency(LOAN_TERMS.minAmount)}</span>
                  <span>{currency(LOAN_TERMS.maxAmount)}</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-baseline mb-3">
                  <label htmlFor="calc-term" className="font-medium text-foreground">Repayment term</label>
                  <span className="font-display text-xl font-bold text-foreground">{term} months</span>
                </div>
                <Slider
                  id="calc-term"
                  aria-label="Repayment term in months"
                  value={[term]}
                  min={LOAN_TERMS.minTermMonths}
                  max={LOAN_TERMS.maxTermMonths}
                  step={6}
                  onValueChange={(v) => setTerm(v[0])}
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>{LOAN_TERMS.minTermMonths} months</span>
                  <span>{LOAN_TERMS.maxTermMonths} months</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-baseline mb-3">
                  <label htmlFor="calc-apr" className="font-medium text-foreground">Representative APR</label>
                  <span className="font-display text-xl font-bold text-foreground">{apr.toFixed(2)}%</span>
                </div>
                <Slider
                  id="calc-apr"
                  aria-label="Annual percentage rate"
                  value={[apr]}
                  min={LOAN_TERMS.minApr}
                  max={LOAN_TERMS.maxApr}
                  step={0.01}
                  onValueChange={(v) => setApr(v[0])}
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>{LOAN_TERMS.minApr}%</span>
                  <span>{LOAN_TERMS.maxApr}%</span>
                </div>
              </div>
            </div>

            <div className="bg-secondary rounded-xl p-6 md:p-8 flex flex-col">
              <p className="text-sm text-muted-foreground mb-1">Estimated monthly repayment</p>
              <p className="font-display text-4xl font-bold text-foreground mb-6">{currency(monthly)}</p>

              <dl className="space-y-3 text-sm border-t border-border pt-5">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Amount borrowed</dt>
                  <dd className="font-medium text-foreground">{currency(amount)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Total interest</dt>
                  <dd className="font-medium text-foreground">{currency(totalInterest)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Total repayable</dt>
                  <dd className="font-medium text-foreground">{currency(totalRepayable)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Number of payments</dt>
                  <dd className="font-medium text-foreground">{term}</dd>
                </div>
              </dl>

              <p className="flex gap-2 text-xs text-muted-foreground mt-6 leading-relaxed">
                <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Results are estimates only and do not constitute an offer of credit. Figures exclude
                  any origination fee and assume all payments are made on time. Your actual rate, fees
                  and repayment will be confirmed after a full application and affordability check.
                </span>
              </p>

              <Button asChild size="lg" className="mt-6 w-full">
                <Link to="/apply">
                  Check your rate <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
